/**
 * Convert a `Date` object to a string formatted correctly for `input[type="date"]`
 * @param {Date} [date] Date to convert. Defaults to today.
 * @returns {string} YYYY-MM-DD format
 */
export function formatInputDate(date = new Date()) {
  const year = date.toLocaleDateString([], { year: "numeric" });
  const month = date.toLocaleDateString([], { month: "2-digit" });
  const day = date.toLocaleDateString([], { day: "2-digit" });
  return `${year}-${month}-${day}`;
}

/**
 * Convert a date-only string to a local Date instead of relying on
 * `new Date("YYYY-MM-DD")`, which browsers interpret as UTC.
 * @param {string | Date | null | undefined} value
 * @returns {Date | null}
 */
export function parseLocalDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalizedValue = value.trim();
  if (normalizedValue === "") {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    return createDateFromInput(normalizedValue);
  }

  const parsedDate = new Date(normalizedValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

/**
 * Format a local calendar day for UI display without shifting across timezones.
 * @param {string | Date | null | undefined} value
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export function formatLocalDateForDisplay(
  value,
  options = { year: "numeric", month: "long", day: "numeric" }
) {
  const date = parseLocalDateValue(value);
  return date ? date.toLocaleDateString("en-US", options) : "";
}

/**
 * @param {string} value
 * @returns {Date}
 */
export function createDateFromInput(value) {
  const parts = value.split("-").map((s) => Number(s));
  if (parts.length < 3) {
    throw new Error("Not a valid string");
  }
  const args = /** @type {const} */ ([parts[0], parts[1] - 1, parts[2]]);
  return new Date(...args);
}

/**
 * @param {Date} a
 * @param {Date} b
 * @returns {boolean}
 */
export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * @param {Date} source The current date you're examining
 * @param {Date} target The date you're comparing against
 * @returns {boolean}
 */
export function isBefore(source, target) {
  if (source < target) {
    if (
      source.getMonth() === target.getMonth() &&
      source.getDate() > target.getDate()
    ) {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * @param {string} input
 * @returns {string | null}
 */
export function formatPhoneNumber(input) {
  const cleaned = ("" + input).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return null;
}

/**
 * Helper event handler for confirming and publishing GA events
 * @param {any} event
 */
export function trackEvent(event) {
  if (!globalThis.dataLayer) {
    globalThis.dataLayer = [];
  }
  globalThis.dataLayer.push(event);
}
